import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaBars, FaTimes, FaSearch, FaHeart, FaUser, FaChevronDown, FaHome, FaThLarge, FaLayerGroup, FaEnvelope, FaFileAlt } from 'react-icons/fa';
import mainService from '../services/mainService';
import { getCartItems, getCartItemCount } from '../utils/cartUtils';
import { useToast } from '../contexts/ToastContext';
import './Header.css';
import config from '../services/config.json';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [categoriesWithSubs, setCategoriesWithSubs] = useState([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Check if user is logged in on component mount and when location changes
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        setUserData(JSON.parse(storedUserData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('userData');
        localStorage.removeItem('authToken');
      }
    } else {
      setUserData(null);
    }
  }, [location.pathname]);

  // Initialize wishlist count when user data changes
  useEffect(() => {
    initWishlist();
  }, [userData]);

  // Initialize cart count on component mount
  useEffect(() => {
    initCartCount();
  }, []);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = (event) => {
      if (event.detail && event.detail.cartItems) {
        updateCartCount(event.detail.cartItems);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Debug: Log wishlist count changes
  useEffect(() => {
    console.log('Wishlist count changed to:', wishlistCount);
  }, [wishlistCount]);

  // Debug: Log cart count changes
  useEffect(() => {
    console.log('Cart count changed to:', cartCount);
  }, [cartCount]);

  // Load categories with subcategories for manual binding in header
  useEffect(() => {
    let isMounted = true;
    mainService
      .categoriesWithSubs()
      .then((data) => {
        if (!isMounted) return;
        if (data) setCategoriesWithSubs(data);
      })
      .catch((err) => {
        console.error('Failed to load categories with subs:', err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize cart count function
  const initCartCount = () => {
    const cartItems = getCartItems();
    updateCartCount(cartItems);
  };

  // Update cart count function
  const updateCartCount = (cartItems) => {
    const totalItems = getCartItemCount();
    setCartCount(totalItems);
  };

  // Initialize wishlist count function
  const initWishlist = async () => {
    setWishlistCount(0);
    
    // Get local storage count first - customer-specific
    let localCount = 0;
    let localItems = [];
    if (userData && userData.CustomerId) {
      const customerSpecificKey = `wishlistProducts_${userData.CustomerId}`;
      const localWishlist = localStorage.getItem(customerSpecificKey);
      localItems = localWishlist ? JSON.parse(localWishlist) : [];
      localCount = localItems.length;
    }
    
    console.log('Local wishlist items:', localItems);
    console.log('Local wishlist count:', localCount);
    
    if (userData && userData.CustomerId > 0) {
      try {
        console.log('Fetching wishlist count for customer ID:', userData.CustomerId);
        const response = await mainService.getTotalWishlist(userData.CustomerId);
        console.log('API wishlist count response:', response);
        
        if (response !== null && response !== undefined && response !== '') {
          // Use API count if available and valid
          const apiCount = parseInt(response) || 0;
          console.log('API count parsed:', apiCount);
          
          // Combine API count with local storage count for accuracy
          const totalCount = Math.max(apiCount, localCount);
          setWishlistCount(totalCount);
          console.log('Set combined wishlist count:', totalCount);
        } else {
          // Fallback to local storage count
          setWishlistCount(localCount);
          console.log('Set wishlist count from local storage (API returned invalid data):', localCount);
        }
      } catch (error) {
        console.error('Error fetching wishlist count:', error);
        // Fallback to local storage count
        setWishlistCount(localCount);
        console.log('Error fallback - Set wishlist count from local storage:', localCount);
      }
    } else {
      // For non-logged in users, show local storage count
      setWishlistCount(localCount);
      console.log('Non-logged user - Set wishlist count from local storage:', localCount);
    }
  };

  // Function to refresh wishlist count (can be called from other components)
  const refreshWishlistCount = () => {
    console.log('Manual refresh triggered');
    initWishlist();
  };

      // Manual refresh function for debugging
  const manualRefreshWishlist = () => {
    console.log('=== MANUAL WISHLIST REFRESH ===');
    console.log('User data:', userData);
    console.log('Current wishlist count state:', wishlistCount);
    
    let localItems = [];
    if (userData && userData.CustomerId) {
      const customerSpecificKey = `wishlistProducts_${userData.CustomerId}`;
      const localWishlist = localStorage.getItem(customerSpecificKey);
      localItems = localWishlist ? JSON.parse(localWishlist) : [];
    }
    console.log('Local storage items:', localItems);
    console.log('Local storage count:', localItems.length);
    
    initWishlist();
  };

  // Test function to add sample wishlist data
  const addTestWishlistData = () => {
    if (!userData || !userData.CustomerId) {
      console.log('No user data available for test wishlist');
      return;
    }
    
    const testItems = [
      {
        Id: 'test1',
        Name: 'Test Product 1',
        Price: 100,
        OfferPrice: 80,
        Image: 'https://via.placeholder.com/150',
        Category: 'Test',
        Rating: 4.5
      },
      {
        Id: 'test2',
        Name: 'Test Product 2',
        Price: 200,
        OfferPrice: 150,
        Image: 'https://via.placeholder.com/150',
        Category: 'Test',
        Rating: 4.0
      }
    ];
    
    const customerSpecificKey = `wishlistProducts_${userData.CustomerId}`;
    localStorage.setItem(customerSpecificKey, JSON.stringify(testItems));
    localStorage.setItem(`wishlistItems_${userData.CustomerId}`, JSON.stringify(['test1', 'test2']));
    
    console.log('Added test wishlist data for customer:', userData.CustomerId, testItems);
    initWishlist();
  };

  // Expose refreshWishlistCount globally so other components can call it
  useEffect(() => {
    window.refreshWishlistCount = refreshWishlistCount;
    window.manualRefreshWishlist = manualRefreshWishlist; // For debugging
    window.addTestWishlistData = addTestWishlistData; // For testing
    return () => {
      delete window.refreshWishlistCount;
      delete window.manualRefreshWishlist;
      delete window.addTestWishlistData;
    };
  }, [userData]);

  const toggleMenu = () => setIsMenuOpen((v) => !v);
  const closeMenu = () => setIsMenuOpen(false);

  const isActive = (path) => location.pathname === path;

  const handleCategoryHover = (name) => setHoveredCategory(name);
  const handleCategoryLeave = () => setHoveredCategory(null);

  const toggleProfile = () => setIsProfileOpen((v) => !v);
  const closeProfile = () => setIsProfileOpen(false);

  // Close profile when route changes
  useEffect(() => {
    closeProfile();
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const profileMenu = document.querySelector('.profile-menu');
      if (isProfileOpen && profileMenu && !profileMenu.contains(event.target)) {
        closeProfile();
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const handleLogout = () => {
    // Clear all authentication data
    localStorage.removeItem('userData');
    localStorage.removeItem('authToken');
    localStorage.removeItem('rememberMe');
    
    // Update state
    setUserData(null);
    setIsProfileOpen(false);
    
    // Redirect to home page
    window.location.href = '/';
  };

  const closeProfileSheet = () => setProfileSheetOpen(false);

  const handleProfileSheetLink = (to) => {
    closeProfileSheet();
    navigate(to);
  };

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    
    if (!userData) {
      showToast('Please login to view your wishlist', 'info', { actionLabel: 'Login', actionTo: '/login' });
      navigate('/login');
      return;
    }

    try {
      const customerId = userData.CustomerId || userData.Id;
      if (!customerId) {
        showToast('Customer ID not found. Please login again.', 'error');
        return;
      }

      navigate('/wishlist');
    } catch (error) {
      console.error('Error navigating to wishlist:', error);
      showToast('Failed to load wishlist. Please try again.', 'error');
    }
  };

  // Categories with subcategories and images (existing local fallback)
  const categories = [];

  return (
    <>
      <header className="header">
        <div className="container header-content">
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="Eilir" className="logo-image" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="nav">
            <ul className="nav-list">
              <li>
                <Link
                  to="/"
                  className={`nav-link ${isActive('/') ? 'active' : ''}`}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/products?id=0&type=all&name=all"
                  className={`nav-link ${isActive('/products') ? 'active' : ''}`}
                >
                  Products
                </Link>
              </li>
              <li>
                <Link
                  to="/categories"
                  className={`nav-link ${isActive('/categories') ? 'active' : ''}`}
                >
                  Categories
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* Search and Cart - search hidden on mobile */}
          <div className="header-actions">
            <div className="search-container header-search-desk">
              <input
                type="text"
                placeholder="Search products..."
                className="search-input"
              />
              <FaSearch className="search-icon" />
            </div>

            <Link to="/wishlist" className="wishlist-icon" onClick={handleWishlistClick}>
              <FaHeart />
              <span className="wishlist-count">{wishlistCount}</span>
            </Link>

            <Link to="/cart" className="cart-icon">
              <FaShoppingCart />
              <span className="cart-count">{cartCount}</span>
            </Link>

            {/* Profile Dropdown - hidden on mobile (use bottom tab Profile) */}
            {userData ? (
              <div className="profile-menu header-profile-desk">
                <button className="profile-trigger" onClick={toggleProfile} aria-haspopup="true" aria-expanded={isProfileOpen}>
                  <FaUser className="profile-icon" />
                  <span className="profile-name">{userData.Name || userData.UserName || 'Customer'}</span>
                  <FaChevronDown className={`chevron ${isProfileOpen ? 'open' : ''}`} />
                </button>
                {isProfileOpen && (
                  <div className="profile-dropdown">
                    <Link to="/account" className="profile-item" onClick={closeProfile}>My Account</Link>
                    <Link to="/my-address" className="profile-item" onClick={closeProfile}>My Address</Link>
                    <Link to="/my-orders" className="profile-item" onClick={closeProfile}>My Orders</Link>
                    <Link 
                      to="/wishlist" 
                      className="profile-item" 
                      onClick={closeProfile}
                    >
                      My Wishlist
                    </Link>
                    <Link to="/change-password" className="profile-item" onClick={closeProfile}>Change Password</Link>
                    <button className="profile-item logout" onClick={handleLogout}>Logout</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-nav header-auth-desk">
                <Link
                  to="/login"
                  className={`auth-link auth-link-signin ${isActive('/login') ? 'active' : ''}`}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="auth-link auth-btn-register"
                >
                  Register
                </Link>
              </div>
            )}

            {/* Mobile Menu Button - hidden when using bottom tab */}
            <button className="mobile-menu-btn header-hamburger" onClick={toggleMenu} aria-label="Menu">
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`nav-mobile ${isMenuOpen ? 'open' : ''}`}>
          <ul className="nav-mobile-list">
            <li>
              <Link
                to="/"
                className={`nav-mobile-link ${isActive('/') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/products?id=0&type=all&name=all"
                className={`nav-mobile-link ${isActive('/products') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                Products
              </Link>
            </li>
            <li>
              <Link
                to="/categories"
                className={`nav-mobile-link ${isActive('/categories') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                Categories
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className={`nav-mobile-link ${isActive('/contact') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/wishlist"
                className={`nav-mobile-link ${isActive('/wishlist') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                Wishlist
              </Link>
            </li>
            <li>
              <Link
                to="/my-orders"
                className={`nav-mobile-link ${isActive('/my-orders') ? 'active' : ''}`}
                onClick={closeMenu}
              >
                My Orders
              </Link>
            </li>
            {userData ? (
              <>
                <li>
                  <Link
                    to="/account"
                    className={`nav-mobile-link ${isActive('/account') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    My Account
                  </Link>
                </li>
                <li>
                  <Link
                    to="/my-address"
                    className={`nav-mobile-link ${isActive('/my-address') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    My Address
                  </Link>
                </li>
                <li>
                  <Link
                    to="/my-orders"
                    className={`nav-mobile-link ${isActive('/my-orders') ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    My Orders
                  </Link>
                </li>
                <li>
                  <button
                    className="nav-mobile-link logout-btn"
                    onClick={() => { handleLogout(); closeMenu(); }}
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li className="auth-mobile-links">
                <Link
                  to="/login"
                  className={`auth-mobile-btn auth-mobile-signin ${isActive('/login') ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="auth-mobile-btn auth-mobile-register"
                  onClick={closeMenu}
                >
                  Register
                </Link>
              </li>
            )}
          </ul>
        </nav>
      </header>

      {/* Mobile Bottom Tab Bar */}
      <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
        <Link to="/" className={`mobile-tab ${isActive('/') ? 'active' : ''}`} onClick={closeProfileSheet}>
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/products?id=0&type=all&name=all" className={`mobile-tab ${location.pathname === '/products' ? 'active' : ''}`} onClick={closeProfileSheet}>
          <FaThLarge />
          <span>Products</span>
        </Link>
        <Link to="/categories" className={`mobile-tab ${isActive('/categories') ? 'active' : ''}`} onClick={closeProfileSheet}>
          <FaLayerGroup />
          <span>Categories</span>
        </Link>
        <button type="button" className={`mobile-tab ${profileSheetOpen ? 'active' : ''}`} onClick={() => setProfileSheetOpen(true)} aria-expanded={profileSheetOpen}>
          <FaUser />
          <span>Profile</span>
        </button>
      </nav>

      {/* Profile / More bottom sheet (mobile) */}
      {profileSheetOpen && (
        <>
          <div className="profile-sheet-backdrop" onClick={closeProfileSheet} aria-hidden="true" />
          <div className="profile-sheet" role="dialog" aria-label="Profile menu">
            <div className="profile-sheet-handle" />
            <h3 className="profile-sheet-title">Menu</h3>
            <div className="profile-sheet-links">
              <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/contact')}>
                <FaEnvelope /> Contact
              </button>
              <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/refund-policy')}>
                <FaFileAlt /> Refund Policy
              </button>
              <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/')}>Home</button>
              <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/products')}>Products</button>
              <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/categories')}>Categories</button>
              {userData ? (
                <>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/account')}>My Account</button>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/my-address')}>My Address</button>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/my-orders')}>My Orders</button>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/wishlist')}>Wishlist</button>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/change-password')}>Change Password</button>
                  <button type="button" className="profile-sheet-link profile-sheet-logout" onClick={() => { handleLogout(); closeProfileSheet(); }}>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/login')}>Sign In</button>
                  <button type="button" className="profile-sheet-link" onClick={() => handleProfileSheetLink('/register')}>Register</button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Sub Navbar with Categories - hidden on mobile */}
      <nav className="sub-navbar sub-navbar-desk">
        <div className="">
          <div className="sub-navbar-content">
            <div className="categories-list">
              {categoriesWithSubs.map((category, index) => {
                const categoryName = typeof category.Name === 'string' ? category.Name : (category.Name && typeof category.Name === 'object' && (category.Name.Name ?? category.Name.name)) ? String(category.Name.Name ?? category.Name.name) : (category.name ?? `Category ${index}`);
                return (
                <div
                  key={category.Id ?? index}
                  className="category-item"
                  onMouseEnter={() => handleCategoryHover(categoryName)}
                  onMouseLeave={handleCategoryLeave}
                >
                  <Link
                    to={`/products?id=${category.Id || 0}&type=category&name=${encodeURIComponent(categoryName)}`}
                    className="category-link"
                  >
                    {categoryName}
                  </Link>
                  
                  {/* Subcategories Dropdown */}
                  {hoveredCategory === categoryName && Array.isArray(category.Subcategories) && category.Subcategories.length > 0 && (
                    <div className="subcategories-dropdown">
                      <div className="subcategories-grid">
                        {category.Subcategories.map((subcategory, subIndex) => {
                          const subName = typeof subcategory === 'object' && subcategory != null
                            ? (typeof subcategory.Name === 'string' ? subcategory.Name : (subcategory.Name?.Name ?? subcategory.Name?.name ?? subcategory.name) ?? '')
                            : String(subcategory);
                          return (
                          <Link
                            key={subIndex}
                            to={`/products?id=${category.Id || 0}&type=category&name=${encodeURIComponent(categoryName)}&subcategory=${encodeURIComponent(subName)}`}
                            className="subcategory-item"
                            onClick={() => setHoveredCategory(null)}
                          >
                            <div className="subcategory-image">
                              <img src={config.apiUrl + category.Image} alt="" />
                            </div>
                            <span className="subcategory-name">{subName}</span>
                          </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* Expose categoriesWithSubs for manual binding (not rendered automatically) */}
            {/* Developer note: use categoriesWithSubs in this component where needed */}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
